import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Crumb } from './layout.js';

/**
 * The breadcrumb slot in the application shell's top bar (D278).
 *
 * The approved redesign shows where the reader is in ONE place: the top bar,
 * beside the search and account controls, not repeated above every page title.
 * Pages keep declaring their breadcrumbs exactly as before, on `PageHeader`;
 * inside the authenticated shell the header hands them to this slot instead of
 * drawing them itself. Outside the shell — the public pages, the client portal,
 * a component test rendering a header alone — there is no slot, and the header
 * draws them inline as it always did.
 */
interface ShellCrumbs {
  readonly crumbs: readonly Crumb[] | null;
  readonly setCrumbs: (crumbs: readonly Crumb[] | null) => void;
}

const ShellCrumbsContext = createContext<ShellCrumbs | null>(null);

export function ShellCrumbsProvider({ children }: { readonly children: ReactNode }) {
  const [crumbs, setCrumbs] = useState<readonly Crumb[] | null>(null);
  const value = useMemo(() => ({ crumbs, setCrumbs }), [crumbs]);
  return <ShellCrumbsContext.Provider value={value}>{children}</ShellCrumbsContext.Provider>;
}

/** What the top bar draws: the current page's breadcrumbs, or `null`. */
export function useShellCrumbs(): readonly Crumb[] | null {
  return useContext(ShellCrumbsContext)?.crumbs ?? null;
}

/**
 * Publishes a page's breadcrumbs to the shell. Returns `true` when a shell took
 * them, so the caller knows not to draw them a second time.
 */
export function usePublishCrumbs(items: readonly Crumb[] | undefined): boolean {
  const slot = useContext(ShellCrumbsContext);
  const setCrumbs = slot?.setCrumbs;
  // Keyed by content, not identity: pages build a fresh array every render.
  const key = items ? items.map((item) => `${item.label}\u0000${item.to ?? ''}`).join('\u0001') : '';
  useLayoutEffect(() => {
    if (!setCrumbs || !items) return undefined;
    setCrumbs(items);
    return () => setCrumbs(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` stands for `items`.
  }, [setCrumbs, key]);
  return Boolean(slot && items);
}
